/**
 * @file     OpenHouseTiles.tsx
 * @purpose  Upcoming open houses as tiles (date, time, Attend). A tile opens
 *           the showing sheet on step 2 with its time filled in, through the
 *           page's ListingActionsProvider when there is one. Where the CTA
 *           config forbids a showing (pending, under contract, sold, expired,
 *           terminated, withdrawn, coming soon) the tiles are plain dates with
 *           no action. Past events drop only after hydration, so the server
 *           list and the first client render agree.
 */
'use client';

import { useState } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { hubColors, transition, reducedMotionSx } from '@/components/hub/hubKitStyles';
import { listingActionsFor, resolveListingState, type ListingState } from '@/core/config/listingActions';
import { layoutTokens } from '@/design-system/tokens/layout';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { useHydratedNow } from '@/hooks/useHydratedNow';
import { formatWeekdayMonthDay } from '@/utils/dateFormat';
import { formatOpenHouseTime } from '@/utils/openHouseTime';
import { upcomingOpenHouses } from '@/utils/upcomingOpenHouses';
import { clockFromLabel } from '@/utils/showingCalendar';
import { useOptionalListingActions, type ShowingPreset } from './ListingActionsContext';
import { ScheduleShowingModal } from './ScheduleShowingModal';

export interface OpenHouseTile {
  openHouseKey: string;
  date: string;
  startTime: string | null;
  endTime: string | null;
  timeZone: string | null;
}

interface Props {
  openHouses: OpenHouseTile[];
  listingTitle: string;
  listingSlug: string;
  listingMlsNumber: string | null;
  /** MLS StandardStatus, for the showing rule when no provider is mounted. */
  standardStatus?: string | null;
  analyticsSurface?: 'slug_page' | 'map_sheet' | 'quick_view';
}

/** True when the state's CTA config lets a visitor ask for a showing. */
export function showingAllowed(state: ListingState): boolean {
  const actions = listingActionsFor(state, { signedIn: false });
  const ids = [actions.primary, ...actions.secondary, ...actions.menu];
  return ids.includes('book_showing') || ids.includes('attend_open_house');
}

const CALENDAR = 'M6 5h12a2.5 2.5 0 0 1 2.5 2.5v10A2.5 2.5 0 0 1 18 20H6a2.5 2.5 0 0 1-2.5-2.5v-10A2.5 2.5 0 0 1 6 5zM3.5 10h17M8 3v4M16 3v4';

export function OpenHouseTiles({ openHouses, listingTitle, listingSlug, listingMlsNumber, standardStatus, analyticsSurface = 'slug_page' }: Props) {
  const { t } = useTranslation();
  const page = useOptionalListingActions();
  const [preset, setPreset] = useState<ShowingPreset | null>(null);
  const now = useHydratedNow();
  const upcoming = upcomingOpenHouses(openHouses, now);

  const state = page?.gated
    ? 'gated'
    : page?.listing.state ?? resolveListingState({ source: 'mls', status: standardStatus ?? 'Active', hasUpcomingOpenHouse: true });
  const canBook = showingAllowed(state);

  if (upcoming.length === 0) return null;

  const open = (next: ShowingPreset) => {
    if (page) page.openShowing(next);
    else setPreset(next);
  };

  return (
    <>
      <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 3 }}>
        {upcoming.map((oh, index) => {
          const start = formatOpenHouseTime(oh.startTime, oh.timeZone);
          const end = formatOpenHouseTime(oh.endTime, oh.timeZone);
          const time = start && end ? `${start} to ${end}` : start || t('listingDetail.openHouses.open', { defaultValue: 'Open' });
          const day = formatWeekdayMonthDay(oh.date);
          const body = (
            <>
              <Box component="svg" aria-hidden viewBox="0 0 24 24" width={20} height={20} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" sx={{ flexShrink: 0, color: hubColors.interactivePrimary }}>
                <path d={CALENDAR} />
              </Box>
              <Box component="span" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box component="span" sx={{ fontSize: 15.5, fontWeight: fontWeightTokens.semibold }}>{day}</Box>
                <Box component="span" sx={{ fontSize: 14, color: hubColors.textSecondary, fontVariantNumeric: 'tabular-nums' }}>{time}</Box>
              </Box>
            </>
          );
          const tileSx = {
            display: 'flex',
            alignItems: 'center',
            gap: 3.5,
            width: '100%',
            boxSizing: 'border-box' as const,
            px: 4,
            py: 3.5,
            borderRadius: `${layoutTokens.radius.md}px`,
            border: `1px solid ${canBook && index === 0 ? hubColors.interactivePrimary : hubColors.borderStrong}`,
            backgroundColor: hubColors.surfaceCard,
            color: hubColors.textPrimary,
            font: 'inherit',
            textAlign: 'left' as const,
          };
          return (
            <Box component="li" key={oh.openHouseKey}>
              {canBook ? (
                <Box
                  component="button"
                  type="button"
                  data-open-house-tile
                  onClick={() => open({ date: oh.date, time: clockFromLabel(start), endTime: clockFromLabel(end) })}
                  aria-label={t('listingDetail.openHouses.tileLabel', {
                    defaultValue: 'Attend the open house on {{day}}, {{time}}',
                    day,
                    time,
                  })}
                  sx={{
                    ...tileSx,
                    cursor: 'pointer',
                    transition: transition('border-color', 'background-color'),
                    ...reducedMotionSx,
                    '@media (hover: hover)': { '&:hover': { backgroundColor: hubColors.interactiveHover } },
                    '&:focus-visible': { outline: `2px solid ${hubColors.interactiveFocusRing}`, outlineOffset: 2 },
                  }}
                >
                  {body}
                  <Box component="span" sx={{ fontSize: 14.5, fontWeight: fontWeightTokens.medium, color: hubColors.link }}>
                    {t('listingDetail.openHouses.attend', { defaultValue: 'Attend' })}
                  </Box>
                </Box>
              ) : (
                <Box data-open-house-tile sx={tileSx}>{body}</Box>
              )}
            </Box>
          );
        })}
      </Box>
      {canBook ? (
        <Box component="p" sx={{ m: 0, mt: 3, fontSize: 14, lineHeight: 1.45, color: hubColors.textSecondary }}>
          {t('showing.details.next', { defaultValue: 'Ray Rasouli will reply by email or phone to confirm a time.' })}
        </Box>
      ) : null}

      {page || !canBook ? null : (
        <ScheduleShowingModal
          open={preset !== null}
          onClose={() => setPreset(null)}
          preselectedDate={preset?.date ?? null}
          preselectedTime={preset?.time ?? null}
          preselectedEndTime={preset?.endTime ?? null}
          listingTitle={listingTitle}
          listingSlug={listingSlug}
          listingMlsNumber={listingMlsNumber}
          analyticsSurface={analyticsSurface}
        />
      )}
    </>
  );
}
